import React, { useRef, useEffect } from "react";
import "./projectSection.css";
import { motion } from "framer-motion";
import driplabIMG from "../../img/macDripLab.png";
import quantixIMG from "../../img/macQuantix.jpg";
import etnaflixIMG from "../../img/etnaflix.jpeg";

const projects = [
  {
    number: "01",
    category: "Front-End",
    title: "DripLab",
    description:
      "A complete WordPress + WooCommerce e-commerce platform featuring an interactive 3D landing page. LAMP stack manually configured on Debian 12.",
    stack: ["WordPress", "Spline"],
    image: driplabIMG,
    link: "https://github.com/JustKIKS/com.DripLab",
  },
  {
    number: "02",
    category: "Full-Stack",
    title: "QuantiX",
    description:
      "Website offering a cryptocurrency wallet and MetaMask account management, user guide, and my first independent UI/UX design project",
    stack: ["React", "JavaScript", "CSS"],
    image: quantixIMG,
    imagePosition: "center 40%",
    imageScale: 1.3,
    link: "https://github.com/JustKIKS/com.Quantix_Lvka",
  },
  {
    number: "03",
    category: "Full-Stack",
    title: "Etnaflix",
    description:
      " A cross-platform mobile app (iOS & Android) developed using React Native and Expo. It allows users to browse a catalog of movies and TV shows, perform searches, and access content by logging in via an external API.",
    stack: [
      "React",
      "Expo",
      "JavaScript",
      "React Navigation",
      "API EXTERNE",
      "Fetch / Axios",
    ],
    image: etnaflixIMG,
    link: "https://github.com/JustKIKS/com.EtnaFlix",
  },
  {
    number: "04",
    category: "Unknown",
    title: "Soon Available",
    description: "A new experience is coming soon...",
    stack: [],
    image: null,
    link: "https://github.com/ton-profil",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", damping: 14, stiffness: 90 },
  },
};

const ProjectSection = () => {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return;

    const updateThumb = () => {
      const { scrollLeft, scrollWidth, clientWidth } = track;
      const scrollable = scrollWidth - clientWidth;
      const ratio = scrollable > 0 ? scrollLeft / scrollable : 0;
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      thumb.style.width = `${thumbWidth}%`;
      thumb.style.left = `${ratio * (100 - thumbWidth)}%`;
    };

    track.scrollLeft = 0;
    updateThumb();
    track.addEventListener("scroll", updateThumb);
    window.addEventListener("resize", updateThumb);
    return () => {
      track.removeEventListener("scroll", updateThumb);
      window.removeEventListener("resize", updateThumb);
    };
  }, []);

  return (
    <section className="projects" id="projects">
      <motion.div
        className="projectsHeader"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <h1 className="projectsTitle">Projects</h1>
        <p className="projectsSubtitle">
          The ones I've had the opportunity to work on!
        </p>
      </motion.div>

      <motion.div
        className="projectsTrack"
        ref={trackRef}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={containerVariants}
      >
        {projects.map((project, i) => (
          <motion.div className="projectCard" key={i} variants={cardVariants}>
            <div className="projectImageWrapper">
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.title}
                  className="projectImage"
                  style={{
                    objectPosition: project.imagePosition || "center center",
                    transform: `scale(${project.imageScale || 1})`,
                    transformOrigin: project.imagePosition || "center center",
                  }}
                />
              ) : (
                <div className="projectImagePlaceholder">No preview</div>
              )}
            </div>

            <div className="projectContent">
              <div className="projectMeta">
                <span className="projectNumber">{project.number}</span>
                <span className="projectDot" />
                <span className="projectCategory">{project.category}</span>
              </div>

              <h2 className="projectTitle">{project.title}</h2>
              <p className="projectDescription">{project.description}</p>

              <div className="projectStack">
                {project.stack.map((tech, j) => (
                  <span className="projectTag" key={j}>
                    {tech}
                  </span>
                ))}
              </div>

              <a
                href={project.link}
                target="_blank"
                rel="noreferrer"
                className="projectLink"
              >
                Voir le projet
                <span className="projectLinkArrow">↗</span>
              </a>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Scrollbar custom positionnée proprement sous les cartes */}
      <div className="projectsScrollbarWrapper">
        <div
          className="projectsScrollbarThumb"
          ref={thumbRef}
          style={{ position: "relative" }}
        />
      </div>
    </section>
  );
};

export default ProjectSection;
